export type AccountRole = "owner" | "player";

export interface SessionUser {
  readonly id: string;
  readonly nickname: string;
  readonly role: AccountRole;
}

export interface VoiceRequestView {
  readonly id: string;
  readonly requesterId: string;
  readonly requesterNickname: string;
  readonly note: string;
  readonly status: string;
  readonly requestedAt: number;
  readonly expiresAt: number;
  readonly sessionId: string | null;
}

export interface VoiceSessionView {
  readonly id: string;
  readonly roomName: string;
  readonly createdBy: string;
  readonly creatorNickname: string;
  readonly authority: "owner" | "approved_request" | "delegated";
  readonly createdAt: number;
  readonly expiresAt: number;
}

export interface VoiceDelegationView {
  readonly id: string;
  readonly accountId: string;
  readonly nickname: string;
  readonly grantedAt: number;
  readonly expiresAt: number;
}

export interface VoiceStateView {
  readonly requests: readonly VoiceRequestView[];
  readonly sessions: readonly VoiceSessionView[];
  readonly delegations: readonly VoiceDelegationView[];
  readonly onlineFriends: readonly SessionUser[];
  readonly isOwner: boolean;
  readonly serverTime: number;
}

export interface LiveKitGrant {
  readonly serverUrl: string;
  readonly roomName: string;
  readonly participantId: string;
  readonly token: string;
  readonly expiresAt: number;
}

interface ApiFailure {
  readonly error?: unknown;
  readonly message?: unknown;
  readonly retryAfterSeconds?: unknown;
  readonly attemptsRemaining?: unknown;
}

const configuredApiOrigin = import.meta.env["VITE_API_ORIGIN"];

export const GORDONLAND_API_ORIGIN = typeof configuredApiOrigin === "string" && configuredApiOrigin.length > 0
  ? configuredApiOrigin.replace(/\/$/u, "")
  : "https://api.gordonland.peterhub.app";

export class GordonLandApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly status: number,
    readonly retryAfterSeconds?: number,
    readonly attemptsRemaining?: number,
  ) {
    super(message);
    this.name = "GordonLandApiError";
  }
}

async function apiRequest<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  if (init.body !== undefined && !headers.has("content-type")) headers.set("content-type", "application/json");
  const response = await fetch(`${GORDONLAND_API_ORIGIN}${path}`, {
    ...init,
    credentials: "include",
    headers,
  });
  const body = await response.json().catch(() => ({})) as T & ApiFailure;
  if (!response.ok) {
    throw new GordonLandApiError(
      typeof body.error === "string" ? body.error : "REQUEST_FAILED",
      typeof body.message === "string" ? body.message : "请求失败，请稍后再试",
      response.status,
      typeof body.retryAfterSeconds === "number" ? body.retryAfterSeconds : undefined,
      typeof body.attemptsRemaining === "number" ? body.attemptsRemaining : undefined,
    );
  }
  return body;
}

export async function getSession(): Promise<SessionUser | null> {
  try {
    const result = await apiRequest<{ readonly user: SessionUser | null }>("/api/auth/session");
    return result.user;
  } catch (error: unknown) {
    if (error instanceof GordonLandApiError && error.status === 401) return null;
    throw error;
  }
}

export async function loginWithNickname(
  nickname: string,
  password: string,
): Promise<{ readonly user: SessionUser; readonly created: boolean }> {
  return apiRequest("/api/auth/nickname", {
    method: "POST",
    body: JSON.stringify({ nickname, password }),
  });
}

export async function logout(): Promise<void> {
  await apiRequest("/api/auth/logout", { method: "POST", body: "{}" });
}

export async function getVoiceState(): Promise<VoiceStateView> {
  return apiRequest("/api/voice/state");
}

export async function requestVoice(note: string): Promise<void> {
  await apiRequest("/api/voice/requests", { method: "POST", body: JSON.stringify({ note }) });
}

export async function cancelVoiceRequest(requestId: string): Promise<void> {
  await apiRequest(`/api/voice/requests/${encodeURIComponent(requestId)}`, { method: "DELETE", body: "{}" });
}

export async function resolveVoiceRequest(
  requestId: string,
  action: "approve" | "deny",
  durationMinutes = 60,
): Promise<void> {
  await apiRequest(`/api/voice/requests/${encodeURIComponent(requestId)}/resolve`, {
    method: "POST",
    body: JSON.stringify({ action, durationMinutes }),
  });
}

export async function startVoiceSession(
  memberNicknames: readonly string[],
  durationMinutes: number,
): Promise<void> {
  await apiRequest("/api/voice/sessions", {
    method: "POST",
    body: JSON.stringify({ memberNicknames, durationMinutes }),
  });
}

export async function addVoiceMember(sessionId: string, nickname: string): Promise<void> {
  await apiRequest(`/api/voice/sessions/${encodeURIComponent(sessionId)}/members`, {
    method: "POST",
    body: JSON.stringify({ nickname }),
  });
}

export async function endVoiceSession(sessionId: string): Promise<void> {
  await apiRequest(`/api/voice/sessions/${encodeURIComponent(sessionId)}`, { method: "DELETE", body: "{}" });
}

export async function grantTemporaryVoice(nickname: string, durationMinutes: number): Promise<void> {
  await apiRequest("/api/voice/delegations", {
    method: "POST",
    body: JSON.stringify({ nickname, durationMinutes }),
  });
}

export async function revokeTemporaryVoice(delegationId: string): Promise<void> {
  await apiRequest(`/api/voice/delegations/${encodeURIComponent(delegationId)}`, { method: "DELETE", body: "{}" });
}

export async function getLiveKitGrant(sessionId: string): Promise<LiveKitGrant> {
  return apiRequest(`/api/voice/sessions/${encodeURIComponent(sessionId)}/token`, { method: "POST", body: "{}" });
}
